
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { TmcT, WriteOffItemI, WriteoffPayloadI, WriteOffPlace } from '@/core/types/tmc.types'
import { WarehouseT } from '@/core/types/warehouses.types'

interface ItemI {
    tmc_id: number
    tmc_name: string
    amount: number
}

// realisations
import { tmc } from '@/core/realisations/tmc.realisation'
import { warehouses } from '@/core/realisations/warehouses.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { required, integer, requiredIf } from '@vuelidate/validators'

// composable
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'
import { useRoute } from 'vue-router'
import { useFormData } from '@/common/composable/useFormData'

type WriteoffPlace = {
    width: string | null
    height: string | null
    length: string | null
}

export default defineComponent({
    components: {
        PageTitle,
        TheSelect,
        TheInput,
        Checkbox,
        TheButton,
        FileReader,
    },
    setup() {
        const { findItemIndexById } = useHelpers()
        const notifications = useNotifications()
        const route = useRoute()
        const { getFormData } = useFormData()

        const { invoice_number, service_type } = route.query

        const state = reactive({
            is_loading: false,
            is_update: false,
            update_item_index: null as number | null,
            warehouse_id: null as number | null,
            invoice_number: null as string | null,
            //
            is_warehouse_consumption: true,
            is_invoice_number: false,
            is_project: false,
            //
            is_soft_package: false,
            is_grid: false,
            is_pallet: false,
            items: [] as ItemI[],
            writeOffPlaces: [] as WriteoffPlace[],
            packageFiles: [] as File[],
        })

        const checkboxValidation = () => {
            return !(
                !state.is_warehouse_consumption &&
                !state.is_pallet &&
                !state.is_grid &&
                !state.is_soft_package
            )
        }

        const isFiles = computed(() => {
            return state.is_invoice_number && (state.is_soft_package || state.is_grid)
        })

        const state_form$ = useVuelidate(
            {
                warehouse_id: { required },
                invoice_number: { required: requiredIf(() => !state.is_warehouse_consumption) },
                is_soft_package: { required: checkboxValidation },
                is_grid: { required: checkboxValidation },
                is_pallet: { required: checkboxValidation },
                packageFiles: { required: requiredIf(() => isFiles.value) },
            },
            state
        )

        const item_state = reactive({
            tmc_id: null as number | null,
            amount: null as number | null,
        })

        const item_form$ = useVuelidate(
            {
                tmc_id: { required },
                amount: { required, integer },
            },
            item_state
        )

        const data_state = reactive({
            tmc: new DataState<TmcT>({}),
            warehouses: new DataState<WarehouseT>({}),
        })

        const loadTMC = async (page: number, search?: string) => {
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true
                const response = await tmc.getList(page, search)
                data_state.tmc.list = response.data

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const loadMoreTMC = async (page: number) => {
            if (page > data_state.tmc.pagination.total_pages) return
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                const response = await tmc.getList(page)
                data_state.tmc.list = data_state.tmc.list.concat(response.data)

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const searchTMC = (search_query: string) => {
            loadTMC(1, search_query)
        }

        const unshiftFindedTMC = async (tmc: TmcT) => {
            await loadTMC(data_state.tmc.pagination.current_page)
            data_state.tmc.list.unshift(tmc)
        }

        const loadWarehouses = async () => {
            data_state.warehouses.pagination.current_page = 1

            try {
                data_state.warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                data_state.warehouses.is_failed = false
                data_state.warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.warehouses.failure.catchError(error)

                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_failed = true
                }
            } finally {
                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_loading = false
                }
            }
        }

        const resetAllCheckboxes = () => {
            state.is_warehouse_consumption = false
            state.is_invoice_number = false
            state.is_project = false
        }

        const checkServiceCheckbox = () => {
            if (service_type === 'grid') state.is_grid = true
            else if (service_type === 'soft_package') state.is_soft_package = true
            else if (service_type === 'pallet') state.is_pallet = true
        }

        const fillInfo = () => {
            if (invoice_number && service_type) {
                resetAllCheckboxes()
                state.is_invoice_number = true
                state.invoice_number = invoice_number.toString()
                checkServiceCheckbox()
            }
        }

        onBeforeMount(() => {
            loadTMC(1)
            loadWarehouses()

            fillInfo()
        })

        const resetItemForm = () => {
            item_state.tmc_id = null
            item_state.amount = null
            item_form$.value.$reset()

            if (state.is_update) {
                state.is_update = false
                state.update_item_index = null
            }
        }

        const isTmcAlreadySelected = (tmc_id: number): boolean => {
            return state.items.some((item) => item.tmc_id === tmc_id)
        }

        const submitItem = () => {
            item_form$.value.$touch()
            if (item_form$.value.$invalid) return

            const tmc_id = item_state.tmc_id as number
            const amount = item_state.amount as number

            const index = findItemIndexById(tmc_id, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = `${data_state.tmc.list[index].name} (${data_state.tmc.list[index].unitName})`

            const is_already_selected = isTmcAlreadySelected(tmc_id)

            if (is_already_selected) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            const item: ItemI = {
                tmc_id,
                tmc_name,
                amount,
            }

            state.items.push(item)

            state.writeOffPlaces.push({
                length: null,
                width: null,
                height: null,
            })

            resetItemForm()
        }

        const removeItem = (item_index: number) => {
            state.items.splice(item_index, 1)
        }

        const removeWriteOffPlace = (place_index: number) => {
            state.writeOffPlaces.splice(place_index, 1)
        }

        const startUpdateItem = (item_index: number) => {
            state.update_item_index = item_index

            item_state.tmc_id = state.items[item_index].tmc_id
            item_state.amount = state.items[item_index].amount

            state.is_update = true
        }

        const updateItem = () => {
            item_form$.value.$touch()
            if (item_form$.value.$invalid) return

            const tmc_id = item_state.tmc_id as number
            const amount = item_state.amount as number

            const index = findItemIndexById(tmc_id, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = `${data_state.tmc.list[index].name} (${data_state.tmc.list[index].unitName})`

            const is_already_selected = isTmcAlreadySelected(tmc_id)
            const maybe_selected_tmc_index = state.items.findIndex(
                (item) => item.tmc_id === item_state.tmc_id
            )

            if (is_already_selected && maybe_selected_tmc_index !== state.update_item_index) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            state.items[state.update_item_index as number].tmc_id = tmc_id
            state.items[state.update_item_index as number].tmc_name = tmc_name
            state.items[state.update_item_index as number].amount = amount

            resetItemForm()
        }

        const resetCheckboxes = () => {
            state.is_warehouse_consumption = true
            state.is_invoice_number = false
            state.is_project = false
        }

        const resetAfterCheckboxChange = () => {
            state.warehouse_id = null
            state.invoice_number = null

            state.is_soft_package = false
            state.is_grid = false
            state.is_pallet = false
            state.items = []
            state.writeOffPlaces = []

            state_form$.value.$reset()
            resetItemForm()
        }

        const resetForm = () => {
            resetCheckboxes()
            resetAfterCheckboxChange()
        }

        const mapItems = (item: ItemI): WriteOffItemI => {
            return {
                inventoryItemId: item.tmc_id,
                amount: Number(item.amount),
            }
        }

        const getAdditionalServiceTypeId = (): number | null => {
            if (state.is_grid) return 6
            if (state.is_soft_package) return 4
            if (state.is_pallet) return 5

            return null
        }

        const placesIsValid = (): boolean => {
            let is_valid = true

            state.writeOffPlaces.forEach((place, index) => {
                let is_place_invalid = false

                const length = Number(place.length)
                const width = Number(place.width)
                const height = Number(place.height)

                if (!length || isNaN(length)) {
                    is_place_invalid = true
                }
                if (!width || isNaN(width)) {
                    is_place_invalid = true
                }
                if (!height || isNaN(height)) {
                    is_place_invalid = true
                }

                if (is_place_invalid) {
                    is_valid = false
                    notifications.notify({
                        title: 'Ошибка валидации',
                        description: `Место №${index + 1} имеет ошибки в заполнении`,
                        type: 'danger',
                    })
                }
            })

            return is_valid
        }

        const mapPlaces = (place: WriteoffPlace): WriteOffPlace => {
            return {
                length: Number(place.length),
                width: Number(place.width),
                height: Number(place.height),
            }
        }

        const getWriteoffPlaces = () => {
            let writeOffPlaces = null

            if (state.writeOffPlaces.find((place) => place.width && place.length && place.height)) {
                writeOffPlaces = state.writeOffPlaces.map(mapPlaces)
            }

            return writeOffPlaces
        }

        const getWriteoffTypeId = () => {
            let writeOffTypeId = 2

            if (state.is_invoice_number) {
                writeOffTypeId = 1
            } else if (state.is_warehouse_consumption) {
                writeOffTypeId = 2
            } else if (state.is_project) {
                writeOffTypeId = 3
            }

            return writeOffTypeId
        }

        const submit = async () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (item_state.tmc_id || item_state.amount) {
                if (state.is_update) {
                    updateItem()
                } else {
                    submitItem()
                }

                if (item_state.tmc_id || item_state.amount) return
            }

            if (state.items.length === 0) {
                return
            }

            if (isNeedPlaces.value) {
                const is_places_valid = placesIsValid()
                if (!is_places_valid) return
            }

            try {
                state.is_loading = true

                const items = state.items.map(mapItems)

                const payload: WriteoffPayloadI = {
                    warehouseId: state.warehouse_id as number,
                    invoiceNumber: state.invoice_number,
                    writeOffTypeId: getWriteoffTypeId(),
                    additionalServiceTypeId: getAdditionalServiceTypeId(),
                    writeOffItems: items,
                    writeOffPlaces: getWriteoffPlaces(),
                }

                if (state.packageFiles.length) {
                    payload.packageFiles = state.packageFiles
                }

                const formData = getFormData(payload)

                await tmc.writeOff(formData)

                notifications.notify({ title: 'Товары списаны', type: 'success' })
                resetForm()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании списания произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const addWriteOffPlace = () => {
            state.writeOffPlaces.push({
                length: null,
                width: null,
                height: null,
            })
        }

        const isNeedPlaces = computed(() => state.is_invoice_number)

        const onWarehouseChange = (warehouse_type: number) => {
            resetAfterCheckboxChange()

            switch (warehouse_type) {
                case 1:
                    state.is_warehouse_consumption = false
                    state.is_project = false
                    break
                case 2:
                    state.is_invoice_number = false
                    state.is_project = false
                    break
                case 3:
                    state.is_invoice_number = false
                    state.is_warehouse_consumption = false
                    break
                default:
                    break
            }
        }

        return {
            state,
            state_form$,
            item_state,
            item_form$,
            data_state,
            //
            loadTMC,
            loadMoreTMC,
            searchTMC,
            unshiftFindedTMC,
            loadWarehouses,
            //
            submitItem,
            removeItem,
            //
            startUpdateItem,
            updateItem,
            //
            submit,
            isNeedPlaces,
            addWriteOffPlace,
            removeWriteOffPlace,
            onWarehouseChange,
            isFiles,
        }
    },
})
